import type { Client, RequestContext } from "../client";
import type { OctopusSpaceRepository } from "../repository";
import { Repository } from "../repository";
import type { CommunicationStyle, DeploymentTargetResource, EnvironmentResource, EnvironmentsSummaryResource, GetEnvironmentsAcrossAllSpacesBffResponse, MachineModelHealthStatus, MachineTenantSummaryResource, MachineTenantTagSummaryResource, NewEnvironmentResource, PaginatedCollection, ProjectResource, ResourceCollection, SummaryResource, VariablesScopedToDocumentResource, } from "../resources";
import type { EnvironmentSettingsMetadata, ExtensionSettingsValues } from "../resources/extensionSettingsValues";
import { BasicRepository } from "./basicRepository";
export type GetEnvironmentRequestV2 = {
    spaceId: string;
    id: string;
};
export type GetEnvironmentsRequestV2 = {
    spaceId: string;
    skip: number;
    take: number;
    partialName?: string;
    type?: EnvironmentType[];
    ids?: string[];
};
export type EnvironmentType = "Static" | "Parent" | "Ephemeral";
export type GetEnvironmentsResponseV2 = PaginatedCollection<EnvironmentV2Resource>;
export type BaseEnvironmentV2Resource = {
    Id: string;
    SpaceId: string;
    Name: string;
    Slug: string;
    Description: string;
    EnvironmentTags?: string[];
};
export type StaticEnvironmentV2Resource = BaseEnvironmentV2Resource & {
    Type: "Static";
    SortOrder: number;
    UseGuidedFailure: boolean;
    AllowDynamicInfrastructure: boolean;
    ExtensionSettings: ExtensionSettingsValues[];
};
export type ParentEnvironmentV2Resource = BaseEnvironmentV2Resource & {
    Type: "Parent";
    SortOrder: number;
    UseGuidedFailure: boolean;
};
export type EphemeralEnvironmentV2Resource = BaseEnvironmentV2Resource & {
    Type: "Ephemeral";
    SortOrder: 2147483647;
    ParentEnvironmentId: string;
};
export type EnvironmentV2Resource = StaticEnvironmentV2Resource | ParentEnvironmentV2Resource | EphemeralEnvironmentV2Resource;
export function isEphemeralEnvironment(environment: EnvironmentV2Resource): environment is EphemeralEnvironmentV2Resource {
    return environment.Type === "Ephemeral";
}
export function isParentEnvironment(environment: EnvironmentV2Resource): environment is ParentEnvironmentV2Resource {
    return environment.Type === "Parent";
}
export function mapFromEnvironmentResourceToEnvironmentV2Resource(environment: EnvironmentResource): StaticEnvironmentV2Resource {
    return {
        ...environment,
        Type: "Static",
        Slug: environment.Slug ?? "",
    };
}
export function mapFromEnvironmentSummaryResourceToEnvironmentSummaryV2Resource(summary: EnvironmentsSummaryResource): EnvironmentsSummaryV2Resource {
    return {
        ...summary,
        EnvironmentSummaries: summary.EnvironmentSummaries.map((envSummary) => ({
            ...envSummary,
            Environment: mapFromEnvironmentResourceToEnvironmentV2Resource(envSummary.Environment),
        })),
    };
}
export class EnvironmentRepository extends BasicRepository<EnvironmentResource, NewEnvironmentResource> {
    constructor(client: Client, requestContext: RequestContext) {
        super("Environments", client, requestContext);
    }
    async create(resource: NewEnvironmentResource, args?: {}): Promise<EnvironmentResource> {
        const environment = await super.create(resource, args);
        this.client.dispatchEvent({ type: "EnvironmentCreated" });
        return environment;
    }
    sort(order: string[]) {
        return this.client.put(this.requestContext, this.client.getLink("EnvironmentSortOrder"), order);
    }
    variablesScopedOnlyToThisEnvironment(environment: EnvironmentResource): Promise<VariablesScopedToDocumentResource> {
        return this.client.get<VariablesScopedToDocumentResource>(this.requestContext, environment.Links["SinglyScopedVariableDetails"]);
    }
    machines(environment: EnvironmentV2Resource, args?: Partial<EnvironmentMachinesArgs>): Promise<ResourceCollection<DeploymentTargetResource>> {
        return this.client.get<ResourceCollection<DeploymentTargetResource>>(this.requestContext, "~/api/{spaceId}/environments/{environmentId}/machines{?skip,take,partialName,roles,isDisabled,healthStatuses,commStyles,tenantIds,tenantTags,shellNames,deploymentTargetTypes}", {
            ...args,
            spaceId: environment.SpaceId,
            environmentId: environment.Id,
        });
    }
    summary(args?: Partial<EnvironmentSummaryArgs>) {
        return this.client.get<EnvironmentsSummaryResource>(this.requestContext, "~/api/{spaceId}/environments/summary{?ids,partialName,machinePartialName,roles,isDisabled,healthStatuses,tenantIds,tenantTags,environmentTags,hideEmptyEnvironments,shellNames,deploymentTargetTypes}", args);
    }
    getMetadata(environment: EnvironmentResource): Promise<EnvironmentSettingsMetadata[]> {
        return this.client.get(this.requestContext, environment.Links["Metadata"], {});
    }
    getByIdV2(request: GetEnvironmentRequestV2): Promise<EnvironmentV2Resource> {
        return this.client.get<EnvironmentV2Resource>(this.requestContext, "~/api/spaces/{spaceId}/environments/{id}/v2", request);
    }
    getV2(request: GetEnvironmentsRequestV2): Promise<GetEnvironmentsResponseV2> {
        return this.client.get<GetEnvironmentsResponseV2>(this.requestContext, "~/api/spaces/{spaceId}/environments/v2{?skip,take,partialName,type}", request);
    }
    summaryV2(request: GetEnvironmentsSummaryRequestV2) {
        return this.client.get<GetEnvironmentsSummaryResponseV2>(this.requestContext, "~/api/spaces/{spaceId}/environments/summary/v2{?ids,partialName,machinePartialName,roles,isDisabled,healthStatuses,tenantIds,tenantTags,environmentTags,hideEmptyEnvironments,shellNames,deploymentTargetTypes,type}", request);
    }
    async getAllEnvironmentAcrossAllSpaces(args?: {
        filterBySpace?: string;
        filterByExcludedSpace?: string;
    }): Promise<EnvironmentResource[]> {
        const res = await this.client.get<GetEnvironmentsAcrossAllSpacesBffResponse>(this.requestContext, "~/bff/environments/acrossallspaces{?filterBySpace,filterByExcludedSpace}", args);
        return res.Environments;
    }
}
export type EnvironmentMachinesArgs = {
    skip: number;
    take: number;
    partialName: string | undefined;
    roles: string | null;
    isDisabled: boolean | undefined;
    healthStatuses: string | null;
    tenantIds: string | null;
    tenantTags: string | null;
    shellNames: string | null;
    deploymentTargetTypes: string | null;
};
export type EnvironmentSummaryArgs = {
    ids: string;
    partialName: string;
    machinePartialName: string;
    roles: string;
    isDisabled: boolean;
    healthStatuses: string;
    tenantIds: string;
    tenantTags: string;
    environmentTags: string;
    hideEmptyEnvironments: boolean;
    shellNames: string;
    deploymentTargetTypes: string;
};
export type GetEnvironmentsSummaryRequestV2 = {
    ids?: string;
    partialName?: string;
    machinePartialName?: string;
    roles?: string;
    isDisabled?: boolean;
    healthStatuses?: string;
    tenantIds?: string;
    tenantTags?: string;
    environmentTags?: string;
    hideEmptyEnvironments?: boolean;
    shellNames?: string;
    deploymentTargetTypes?: string;
    type?: EnvironmentType[];
};
export type GetEnvironmentsSummaryResponseV2 = {
    EnvironmentSummaries: EnvironmentSummaryV2Resource[];
    MachineTenantSummaries: MachineTenantSummaryResource;
    MachineTenantTagSummaries: MachineTenantTagSummaryResource;
    TotalMachines: number;
    TotalDisabledMachines: number;
    MachineHealthStatusSummaries: Record<MachineModelHealthStatus, number>;
    MachineEndpointSummaries: Record<CommunicationStyle, number>;
    DeploymentTargetSummaries: Record<string, number>;
    TentacleUpgradesRequired: boolean;
    MachineIdsForCalamariUpgrade: string[];
    MachineIdsForTentacleUpgrade: string[];
};
export interface EnvironmentsSummaryV2Resource extends GetEnvironmentsSummaryResponseV2 {
}
export interface EnvironmentSummaryV2Resource extends SummaryResource {
    Environment: EnvironmentV2Resource;
    MachineTenantSummaries: MachineTenantSummaryResource;
    MachineTenantTagSummaries: MachineTenantTagSummaryResource;
}
export async function getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled: boolean, repository: OctopusSpaceRepository, ids?: string[]): Promise<EnvironmentV2Resource[]> {
    let environments: EnvironmentV2Resource[] = [];
    if (ephemeralEnvironmentsAreEnabled) {
        environments = await getStaticAndParentEnvironments(repository, ids);
    }
    else {
        const environmentsResponse = await repository.Environments.all({ ids: ids });
        environments = environmentsResponse.map(mapFromEnvironmentResourceToEnvironmentV2Resource);
    }
    return environments;
}
export async function getStaticAndParentEnvironments(repository: OctopusSpaceRepository, ids?: string[]): Promise<EnvironmentV2Resource[]> {
    let environments: EnvironmentV2Resource[] = [];
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const environmentsV2Response = await repository.Environments.getV2({ spaceId: repository.spaceId!, skip: 0, take: Repository.takeAll, type: ["Static", "Parent"] });
    environments = environmentsV2Response.Items;
    if (ids) {
        environments = environments.filter((environment) => ids?.includes(environment.Id));
    }
    return environments;
}
export async function getParentEnvironments(ephemeralEnvironmentsAreEnabled: boolean, repository: Repository, project: ProjectResource): Promise<ParentEnvironmentV2Resource[]> {
    if (!ephemeralEnvironmentsAreEnabled) {
        return [];
    }
    const environmentsV2Response = await repository.Environments.getV2({ spaceId: project.SpaceId, skip: 0, take: Repository.takeAll, type: ["Parent"] });
    // Although we request only Parent environments via the type filter, apply the runtime type guard
    // to ensure correct typing is correct.
    const parentEnvironments = environmentsV2Response.Items.filter(isParentEnvironment);
    return parentEnvironments;
}
